<template>
  <div class="public-booking">
    <div class="public-booking__blob public-booking__blob--top" />
    <div class="public-booking__blob public-booking__blob--bottom" />

    <v-container class="py-8 public-booking__container">
      <div class="d-flex align-center mb-8">
        <img :src="logo" alt="Логотип" class="public-booking__logo">
      </div>

      <div class="text-center mb-8">
        <h1 class="public-booking__title mb-2">{{ pageTitle }}</h1>
        <div v-if="pageSubtitle" class="text-body-1 grey--text text--darken-1">{{ pageSubtitle }}</div>
      </div>

      <div v-if="headerExpert" class="d-flex align-center justify-center mb-8">
        <v-avatar size="56" color="#483CB9" class="mr-3">
          <img v-if="headerExpert.photo_url" :src="headerExpert.photo_url" :alt="expertName(headerExpert)">
          <span v-else class="white--text text-subtitle-1">{{ initials(expertName(headerExpert)) }}</span>
        </v-avatar>
        <div>
          <div class="font-weight-bold text-body-1">{{ expertName(headerExpert) }}</div>
          <div v-if="headerExpert.short_description" class="text-caption grey--text">
            {{ headerExpert.short_description }}
          </div>
        </div>
      </div>

      <div v-if="linkMode && !ready && (link.loading || catalogLoading)" class="text-center grey--text py-8">
        Загружаем…
      </div>

      <v-sheet v-else-if="link.unavailable" rounded="xl" class="pa-8 text-center public-booking__result">
        <v-icon size="48" color="#9e9e9e" class="mb-3">mdi-link-variant-off</v-icon>
        <div class="text-h6 font-weight-bold mb-2">Ссылка недоступна</div>
        <div class="text-body-2 grey--text text--darken-1">
          Возможно, ссылка отключена или в ней опечатка. Уточните её у того, кто её прислал.
        </div>
      </v-sheet>

      <v-sheet v-else-if="link.error" rounded="xl" class="pa-8 text-center public-booking__result">
        <div class="text-h6 font-weight-bold mb-2">Не удалось загрузить запись</div>
        <div class="text-body-2 grey--text text--darken-1">Проверьте соединение и попробуйте ещё раз.</div>
        <v-btn text color="#483CB9" class="mt-4 text-capitalize" @click="init">Повторить</v-btn>
      </v-sheet>

      <template v-else-if="booking.result">
        <v-sheet rounded="xl" class="pa-8 text-center public-booking__result">
          <v-icon size="48" color="#009E60" class="mb-3">mdi-check-circle-outline</v-icon>
          <div class="text-h6 font-weight-bold mb-2">Заявка принята</div>
          <div class="text-body-2 grey--text text--darken-1">
            Мы отправили подтверждение на указанную почту. Ссылка для отмены записи тоже будет в письме.
          </div>
          <v-btn text color="#483CB9" class="mt-4 text-capitalize" @click="startOver">
            Записаться ещё раз
          </v-btn>
        </v-sheet>
      </template>

      <v-sheet
        v-else-if="linkMode && link.data && !catalogLoading && !catalogServices.length"
        rounded="xl"
        class="pa-8 text-center public-booking__result"
      >
        <div class="text-h6 font-weight-bold mb-2">Нет доступных вариантов по этой ссылке</div>
        <div class="text-body-2 grey--text text--darken-1">Попробуйте зайти позже.</div>
      </v-sheet>

      <template v-else-if="ready">
        <div v-if="choices.length > 1" class="d-flex flex-wrap justify-center mb-6 public-booking__choices">
          <button
            v-for="choice in choicesWithAll"
            :key="choice.id === null ? 'all' : choice.id"
            type="button"
            class="public-booking__choice"
            :class="{ 'public-booking__choice--active': choice.id === selectedChoice }"
            @click="onSelectChoice(choice.id)"
          >
            {{ choice.label }}
          </button>
        </div>

        <v-alert
          v-if="conflictNotice"
          type="warning"
          dense
          text
          rounded="lg"
          dismissible
          class="mb-6"
          @input="conflictNotice = false"
        >
          Это время больше недоступно. Мы обновили расписание — выберите другое время.
        </v-alert>

        <PublicMonthGrid
          :month="currentMonth"
          :selected-date="selectedDate"
          :slot-dates="monthAvailabilityDates"
          class="mb-6"
          @update:month="currentMonth = $event"
          @select-date="onSelectDate"
        />

        <div v-if="loading" class="text-center grey--text py-8">
          Ищем свободное время…
        </div>
        <div v-else-if="searched && !optionsByExpert.length" class="text-center grey--text py-8">
          На эту дату свободного времени нет. Попробуйте выбрать другой день.
        </div>

        <PublicExpertCard
          v-for="group in optionsByExpert"
          :key="group.expert.id"
          :expert="group.expert"
          :services="group.services"
          class="mb-4"
          @select-slot="openBooking"
        />
      </template>
    </v-container>

    <GuestBookingDialog
      v-model="dialog"
      :option="selectedOption"
      :loading="booking.loading"
      :error="booking.error"
      :initial-contacts="savedContacts"
      @submit="onSubmitBooking"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { pick } from 'lodash';
import PublicMonthGrid from '@/components/Calendar/Public/PublicMonthGrid.vue';
import PublicExpertCard from '@/components/Calendar/Public/PublicExpertCard.vue';
import GuestBookingDialog from '@/components/Calendar/Public/GuestBookingDialog.vue';

// Одна страница на два маршрута: /booking — общий календарь, /booking/:code — ограниченная
// ссылка. Для ссылки основной объект (эксперт или тип услуги) зафиксирован, а гость выбирает
// только среди разрешённых ссылкой вариантов (услуги эксперта или эксперты типа услуги).
export default {
  name: 'PublicBooking',
  components: { PublicMonthGrid, PublicExpertCard, GuestBookingDialog },
  props: {
    code: { type: String, default: '' },
  },
  data() {
    return {
      currentMonth: this.$moment().format('YYYY-MM'),
      selectedDate: this.$moment().format('YYYY-MM-DD'),
      searched: false,
      dialog: false,
      selectedOption: null,
      selectedChoice: null, // id выбранной услуги (ссылка на эксперта) или эксперта (ссылка на тип), null — все
      savedContacts: null,
      conflictNotice: false,
    };
  },
  computed: {
    ...mapGetters('publicCalendar', [
      'loading',
      'optionsByExpert',
      'booking',
      'monthAvailabilityDates',
      'link',
      'catalogServices',
      'catalogExperts',
      'catalogLoading',
    ]),
    ...mapGetters('auth', ['isTA']),
    // Тот же паттерн выбора лого по сборке, что и в DashboardLayout.vue — isTA не зависит
    // от сессии (читает VUE_APP_MODE на этапе сборки), доступен и на публичном роуте.
    logo() {
      // eslint-disable-next-line global-require, import/no-dynamic-require
      return require(this.isTA ? '@/assets/images/logoTA_RU.svg' : '@/assets/images/logo.svg');
    },
    linkMode() {
      return Boolean(this.code);
    },
    linkKind() {
      return this.link.data ? this.link.data.kind : null;
    },
    // Карточка эксперта в шапке: у ссылки на эксперта — всегда, у ссылки на тип услуги — когда
    // по ней остался единственный эксперт (кнопок выбора нет, и иначе гость не видит, к кому запись).
    headerExpert() {
      if (this.link.unavailable) return null;
      if (this.linkKind === 'expert') return this.link.data.expert;
      if (this.linkKind === 'service_type' && this.catalogExperts.length === 1) return this.catalogExperts[0];
      return null;
    },
    pageTitle() {
      if (this.linkKind === 'service_type' && !this.link.unavailable) return this.link.data.service_type.name;
      return 'Запись на услуги';
    },
    pageSubtitle() {
      // На экранах «ссылка недоступна»/ошибки/«нет вариантов» выбирать нечего — подсказку не показываем.
      if (this.linkMode && !this.ready) return '';
      if (this.linkKind && this.choices.length <= 1) return 'Выберите дату — покажем свободное время.';
      if (this.linkKind === 'expert') return 'Выберите услугу и дату — покажем свободное время.';
      if (this.linkKind === 'service_type') return 'Выберите эксперта и дату — покажем свободное время.';
      return 'Выберите дату — покажем доступное время экспертов.';
    },
    // Пока ссылка не разрешена и каталог не загружен, слоты не запрашиваем: без этого гость
    // на мгновение увидел бы пустую сетку вместо состояния «ссылка недоступна».
    ready() {
      if (!this.linkMode) return true;
      return Boolean(this.link.data) && !this.link.unavailable && this.catalogServices.length > 0;
    },
    choices() {
      if (this.linkKind === 'expert') {
        return this.catalogServices.map((service) => ({
          id: service.id,
          label: `${service.service_type.name} · ${service.max_duration_minutes} мин`,
        }));
      }
      if (this.linkKind === 'service_type') {
        return this.catalogExperts.map((expert) => ({ id: expert.id, label: this.expertName(expert) }));
      }
      return [];
    },
    choicesWithAll() {
      return [{ id: null, label: 'Все' }, ...this.choices];
    },
    filters() {
      if (this.linkKind === 'expert') {
        return { expert: this.link.data.expert.id, service: this.selectedChoice };
      }
      if (this.linkKind === 'service_type') {
        return { serviceType: this.link.data.service_type.id, expert: this.selectedChoice };
      }
      return {};
    },
  },
  watch: {
    code: {
      immediate: true,
      handler() {
        this.init();
      },
    },
    currentMonth() {
      this.reloadMonth();
    },
  },
  methods: {
    ...mapActions('publicCalendar', [
      'searchAvailability',
      'createAppointment',
      'resetBooking',
      'resetOptions',
      'loadMonthAvailability',
      'setLinkCode',
      'resolveLink',
      'loadCatalog',
    ]),
    async init() {
      this.setLinkCode(this.code);
      this.selectedChoice = null;
      this.selectedOption = null;
      this.dialog = false;
      this.searched = false;
      this.savedContacts = null;
      this.conflictNotice = false;
      if (this.linkMode && !(await this.loadLink())) return;
      this.refreshSlots();
    },
    // Метаданные + каталог ссылки. false — дальше идти некуда (ссылка недоступна, ошибка,
    // пустой каталог или за время запроса код в URL уже сменился).
    async loadLink() {
      const { code } = this;
      const link = await this.resolveLink();
      if (!link || code !== this.code) return false;
      await this.loadCatalog();
      if (code !== this.code || !this.ready) return false;
      // Выбор мог стать недоступен после обновления каталога — сбрасываем, а не держим
      // фильтр, который бэк всё равно отсечёт.
      if (this.selectedChoice !== null && !this.choices.some((c) => c.id === this.selectedChoice)) {
        this.selectedChoice = null;
      }
      if (this.choices.length === 1) this.selectedChoice = this.choices[0].id;
      return true;
    },
    refreshSlots() {
      this.search();
      this.reloadMonth();
    },
    reloadMonth() {
      if (!this.ready) return;
      this.loadMonthAvailability({ month: this.currentMonth, ...this.filters });
    },
    onSelectDate(date) {
      // Прошедшие дни не бронируются — молча игнорируем клик (PublicMonthGrid тоже их не эмитит,
      // но дублируем проверку здесь на случай прямого вызова метода).
      if (date < this.$moment().format('YYYY-MM-DD')) return;
      this.selectedDate = date;
      this.search();
    },
    onSelectChoice(id) {
      if (this.selectedChoice === id) return;
      this.selectedChoice = id;
      this.refreshSlots();
    },
    async search() {
      if (!this.selectedDate || !this.ready) return;
      this.searched = true;
      try {
        await this.searchAvailability({ date: this.selectedDate, ...this.filters });
      } catch (err) {
        // 404 по ссылке — она отключена или основной объект пропал: перепроверяем код,
        // страница сама переключится на «Ссылка недоступна».
        if (this.linkMode && err.status === 404) this.loadLink();
      }
    },
    openBooking(option) {
      this.conflictNotice = false;
      this.selectedOption = option;
      this.dialog = true;
    },
    async onSubmitBooking(payload) {
      try {
        await this.createAppointment(payload);
        this.dialog = false;
        this.savedContacts = null;
        // Слот мог перестать быть доступен для других гостей — обновляем список и точки на сетке.
        this.refreshSlots();
      } catch (err) {
        // 409 — время заняли или ссылка больше не разрешает этот вариант; 404 — ссылка/слот
        // пропали. Сохраняем контакты, обновляем всё под тем же кодом и просим выбрать заново.
        // Остальные ошибки (400) остаются в диалоге, как раньше.
        if (err.status !== 409 && err.status !== 404) return;
        this.savedContacts = pick(payload, ['guest_first_name', 'guest_last_name', 'guest_email', 'telegram_username']);
        this.dialog = false;
        this.resetBooking();
        this.conflictNotice = true;
        if (this.linkMode && !(await this.loadLink())) return;
        this.refreshSlots();
      }
    },
    startOver() {
      this.resetBooking();
      this.resetOptions();
      this.searched = false;
      this.selectedOption = null;
      this.refreshSlots();
    },
    expertName(expert) {
      return `${expert.first_name || ''} ${expert.last_name || ''}`.trim();
    },
    initials(name) {
      return name.split(' ')
        .filter(Boolean)
        .map((part) => part[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
    },
  },
};
</script>

<style lang="scss" scoped>
.public-booking {
  position: relative;
  min-height: 100vh;
  background: #F7F6FB;
  overflow: hidden;

  &__container {
    max-width: 880px;
    position: relative;
    z-index: 1;
  }

  &__logo {
    height: 26px;
  }

  &__title {
    font-size: 32px;
    font-weight: 700;
    color: #483CB9;
    letter-spacing: -0.01em;
  }

  &__result {
    box-shadow: 0 8px 24px rgba(72, 60, 185, 0.08);
  }

  // Выбор услуги/эксперта внутри ограниченной ссылки — тот же pill-язык, что и у слотов
  // времени в PublicExpertCard, только активный вариант залит.
  &__choices {
    gap: 8px;
  }

  &__choice {
    border: 1px solid #483CB9;
    border-radius: 999px;
    padding: 8px 16px;
    background: #fff;
    color: #483CB9;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 150ms ease, color 150ms ease;

    &:hover,
    &--active {
      background: #483CB9;
      color: #fff;
    }
  }

  // Декоративные размытые пятна фона — только форма/цвет бренда, без картинок,
  // повторяют угловые акценты макета.
  &__blob {
    position: absolute;
    border-radius: 50%;
    background: #483CB9;
    opacity: 0.08;
    filter: blur(60px);
    pointer-events: none;
    z-index: 0;

    &--top {
      width: 420px;
      height: 420px;
      top: -180px;
      right: -140px;
    }

    &--bottom {
      width: 360px;
      height: 360px;
      bottom: -160px;
      left: -140px;
    }
  }
}
</style>
